import React from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useWhatsAppPopup } from "@/contexts/WhatsAppPopupContext";

const HeroSection = React.forwardRef<HTMLDivElement>((props, ref) => {
  const { openPopup } = useWhatsAppPopup();
  const { scrollYProgress } = useScroll({
    target: ref as React.RefObject<HTMLDivElement>,
    offset: ["start start", "end end"],
  });

  // Text animation and blur remain controlled by this component's scroll progress
  const textY = useTransform(scrollYProgress, [0, 0.3], [50, 0]);
  const textOpacity = useTransform(scrollYProgress, [0, 0.3], [0, 1]);
  const videoBlur = useTransform(
    scrollYProgress,
    [0, 0.5],
    ["blur(0px)", "blur(12px)"]
  );

  // Sub-headline e CTAs — aparecem junto com "ONDE A BUROCRACIA TERMINA."
  const subOpacity = useTransform(scrollYProgress, [0.2, 0.4], [0, 1]);
  const subY = useTransform(scrollYProgress, [0.2, 0.4], [30, 0]);
  const ctaOpacity = useTransform(scrollYProgress, [0.3, 0.5], [0, 1]);
  const ctaY = useTransform(scrollYProgress, [0.3, 0.5], [30, 0]);

  return (
    <section ref={ref} className="relative h-[200vh] w-full">
      <div className="sticky top-0 h-screen w-full">
        {/* Gradient overlay for Header readability */}
        <div className="absolute top-0 left-0 w-full h-24 bg-gradient-to-b from-black/80 to-transparent z-40" />

        {/* Video Background */}
        <motion.div
          style={{ filter: videoBlur }}
          className="absolute inset-0 z-0"
        >
          <div className="absolute inset-0 z-10 bg-black/40" />
          <video
            autoPlay
            muted
            loop
            playsInline
            className="absolute inset-0 w-full h-full object-cover"
          >
            <source src="/videos/VerticalHero.mp4" type="video/mp4" />
          </video>
        </motion.div>

        {/* Content */}
        <div className="relative z-20 h-full flex flex-col items-center justify-center text-primary-foreground text-center px-6">
          <div className="font-heading uppercase font-extrabold tracking-widest text-4xl md:text-5xl lg:text-6xl">
            <h1>SUA LIBERDADE COMEÇA...</h1>
            <motion.h1
              style={{ y: textY, opacity: textOpacity }}
              className="mt-4 text-gold-shimmer"
            >
              ONDE A BUROCRACIA TERMINA.
            </motion.h1>
          </div>

          {/* Sub-headline - Aparece junto com o texto principal */}
          <motion.p
            style={{ opacity: subOpacity, y: subY }}
            className="mt-6 text-base sm:text-lg text-primary-foreground/70 font-body tracking-wider uppercase"
          >
            Escola Náutica{" "}
            <span className="text-gold/60 mx-2">·</span>{" "}
            Despachante
          </motion.p>

          {/* CTAs - Aparecem logo depois */}
          <motion.div
            style={{ opacity: ctaOpacity, y: ctaY }}
            className="mt-8 flex flex-col sm:flex-row items-center gap-4"
          >
            <button
              onClick={() => openPopup("generic")}
              className="btn-gold px-8 py-3.5 rounded-lg text-sm font-medium text-primary-foreground inline-flex items-center gap-2 group"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" className="w-4 h-4">
                <path d="M13.601 2.326A7.85 7.85 0 0 0 7.994 0C3.627 0 .068 3.558.064 7.926c0 1.399.366 2.76 1.057 3.965L0 16l4.204-1.102a7.9 7.9 0 0 0 3.79.965h.004c4.368 0 7.926-3.558 7.93-7.93A7.9 7.9 0 0 0 13.6 2.326zM7.994 14.521a6.6 6.6 0 0 1-3.356-.92l-.24-.144-2.494.654.666-2.433-.156-.251a6.56 6.56 0 0 1-1.007-3.505c0-3.626 2.957-6.584 6.591-6.584a6.56 6.56 0 0 1 4.66 1.931 6.56 6.56 0 0 1 1.928 4.66c-.004 3.639-2.961 6.592-6.592 6.592m3.615-4.934c-.197-.099-1.17-.578-1.353-.646-.182-.065-.315-.099-.445.099-.133.197-.513.646-.627.775-.114.133-.232.148-.43.05-.197-.1-.836-.308-1.592-.985-.59-.525-.985-1.175-1.103-1.372-.114-.198-.011-.304.088-.403.087-.088.197-.232.296-.346.1-.114.133-.198.198-.33.065-.134.034-.248-.015-.347-.05-.099-.445-1.076-.612-1.47-.16-.389-.323-.335-.445-.34-.114-.007-.247-.007-.38-.007a.73.73 0 0 0-.529.247c-.182.198-.691.677-.691 1.654s.71 1.916.81 2.049c.098.133 1.394 2.132 3.383 2.992.47.205.84.326 1.129.418.475.152.904.129 1.246.08.38-.058 1.171-.48 1.338-.943.164-.464.164-.86.114-.943-.049-.084-.182-.133-.38-.232" />
              </svg>
              Fale com Especialista
            </button>
            <a
              href="#servicos"
              className="px-8 py-3.5 rounded-lg text-sm font-medium text-primary-foreground/80 border border-primary-foreground/30 hover:bg-primary-foreground/10 transition-colors duration-200 inline-flex items-center gap-2"
            >
              Nossos Serviços
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
              </svg>
            </a>
          </motion.div>
        </div>
      </div>
    </section>
  );
});

export default HeroSection;
